import { showUserAlert } from "../utils/userErrorMessage";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../Component/Header";
import { supabase } from "../supabase/client";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const formatDateTime = (value) => value
  ? new Date(value).toLocaleString("ko-KR", { timeZone: "Asia/Seoul", hour12: false })
  : "-";

export default function AppNoticeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const company = (localStorage.getItem("email") || "").trim();
  const [notice, setNotice] = useState(null);
  const [readers, setReaders] = useState([]);
  const [readersLoading, setReadersLoading] = useState(false);
  const [form, setForm] = useState({ title: "", content: "" });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadNotice = useCallback(async () => {
    if (!company || !id) {
      showUserAlert("공지사항 또는 회사 정보를 찾을 수 없습니다.");
      navigate("/app-notices", { replace: true });
      return;
    }

    setLoading(true);
    const { data, error } = await supabase
      .from("app_notices")
      .select("id, title, content, created_at, updated_at")
      .eq("id", id)
      .eq("company", company)
      .maybeSingle();

    if (error || !data) {
      console.error("앱 공지사항 상세 조회 실패:", error);
      showUserAlert("공지사항을 찾을 수 없거나 접근 권한이 없습니다.");
      navigate("/app-notices", { replace: true });
    } else {
      setNotice(data);
      setForm({ title: data.title || "", content: data.content || "" });

      setReadersLoading(true);
      const { data: readData, error: readError } = await supabase
        .from("app_notice_reads")
        .select("user_id, driver_name, read_at, notice_updated_at")
        .eq("notice_id", data.id)
        .eq("company", company)
        .eq("notice_updated_at", data.updated_at)
        .order("read_at", { ascending: true });
      if (readError) {
        console.error("앱 공지사항 확인 완료 내역 조회 실패:", readError);
        setReaders([]);
      } else {
        setReaders(readData || []);
      }
      setReadersLoading(false);
    }
    setLoading(false);
  }, [company, id, navigate]);

  useEffect(() => { loadNotice(); }, [loadNotice]);

  const handleUpdate = async () => {
    const title = form.title.trim();
    const content = form.content.trim();
    if (!title) return showUserAlert("제목을 입력해 주세요.");
    if (!content) return showUserAlert("내용을 입력해 주세요.");
    if (!window.confirm("공지사항을 수정하시겠습니까?")) return;

    setSaving(true);
    try {
      const { data, error } = await supabase
        .from("app_notices")
        .update({ title, content })
        .eq("id", id)
        .eq("company", company)
        .select("id, title, content, created_at, updated_at")
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error("수정된 공지사항이 없습니다.");

      setNotice(data);
      setForm({ title: data.title || "", content: data.content || "" });
      setReaders([]);
      setEditing(false);
      showUserAlert("공지사항이 수정되었습니다.");
    } catch (error) {
      console.error("앱 공지사항 수정 실패:", error);
      showUserAlert("공지사항 수정에 실패했습니다: " + (error.message || "알 수 없는 오류"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("이 공지사항을 삭제하시겠습니까?")) return;

    setDeleting(true);
    try {
      const { data, error } = await supabase
        .from("app_notices")
        .delete()
        .eq("id", id)
        .eq("company", company)
        .select("id")
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error("삭제된 공지사항이 없습니다.");

      showUserAlert("공지사항이 삭제되었습니다.");
      navigate("/app-notices", { replace: true });
    } catch (error) {
      console.error("앱 공지사항 삭제 실패:", error);
      showUserAlert("공지사항 삭제에 실패했습니다: " + (error.message || "알 수 없는 오류"));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-1">
      <Header />
      <main className="space-y-4 px-4 pb-6 pt-2">
        <h1 className="text-lg font-semibold">앱 공지사항 상세</h1>

        {loading ? (
          <div className="py-8 text-center text-sm">조회 중...</div>
        ) : notice && (
          <section className="space-y-4">
            {editing ? (
              <>
                <label className="block text-sm font-medium">
                  제목
                  <Input
                    className="mt-1 w-full"
                    value={form.title}
                    maxLength={100}
                    onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
                  />
                </label>
                <label className="block text-sm font-medium">
                  내용
                  <textarea
                    className="mt-1 min-h-48 w-full resize-y rounded border border-gray-300 px-3 py-2 text-sm"
                    value={form.content}
                    maxLength={5000}
                    onChange={(event) => setForm((current) => ({ ...current, content: event.target.value }))}
                  />
                </label>
              </>
            ) : (
              <>
                <div className="border-b pb-3">
                  <h2 className="text-base font-semibold">{notice.title}</h2>
                  <p className="mt-1 text-xs text-gray-500">등록일 {formatDateTime(notice.created_at)}</p>
                </div>
                <div className="min-h-48 whitespace-pre-wrap break-words px-1 py-2 text-sm">
                  {notice.content}
                </div>
                <div className="border-t pt-4" aria-label="확인 완료한 사람">
                  <h3 className="text-sm font-semibold">확인 완료 ({readers.length}명)</h3>
                  {readersLoading ? (
                    <p className="mt-2 text-sm text-gray-500">확인 내역 조회 중...</p>
                  ) : readers.length === 0 ? (
                    <p className="mt-2 text-sm text-gray-500">아직 확인 완료한 사람이 없습니다.</p>
                  ) : (
                    <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-2 text-sm">
                      {readers.map((reader) => (
                        <li key={reader.user_id}>{reader.driver_name || "이름 미등록"}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </>
            )}

            <div className="flex items-center justify-between gap-2">
              <Button type="button" variant="outline" onClick={() => navigate("/app-notices")}>목록으로</Button>
              <div className="flex gap-2">
                {editing ? (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={saving}
                      onClick={() => {
                        setForm({ title: notice.title || "", content: notice.content || "" });
                        setEditing(false);
                      }}
                    >
                      취소
                    </Button>
                    <Button type="button" disabled={saving} onClick={handleUpdate}>
                      {saving ? "저장 중..." : "저장"}
                    </Button>
                  </>
                ) : (
                  <Button type="button" onClick={() => setEditing(true)}>수정</Button>
                )}
                <Button
                  type="button"
                  disabled={saving || deleting}
                  onClick={handleDelete}
                  className="bg-red-600 hover:bg-red-700 disabled:opacity-50"
                >
                  {deleting ? "삭제 중..." : "삭제"}
                </Button>
              </div>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
